import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth.service';
import {Router} from '@angular/router';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeInUpBigOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {UserService} from '../../../services/user.service';
import {Subscription} from 'rxjs';
import {EventsService} from '../../../services/events.service';
import {environment} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';
import {UserInfoModel} from '../../../services/models/user-info.model';
import {DelegateSimpleModel, DelegateSummaryModel} from '../../team/models/delegate.model';
import {TeamService} from '../../team/service/team.service';
import {NavService} from '../../../services/nav.service';
import {ProgressModel} from '../../../services/models/progress.model';
import {ProgressService} from '../../../services/progress.service';
import {Platform} from '@ionic/angular';

@Component({
  selector: 'app-team-list-page',
  templateUrl: 'team-list.page.html',
  styleUrls: ['team-list.page.scss'],
  animations: [
    fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
    fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
    fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
    fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
    fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
  ],
})
export class TeamListPage implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];

  environment = null;
  menuVisible = true;

  userType = null;

  userInfo: UserInfoModel = null;
  userInfoPromise: Promise<UserInfoModel> = null;
  userInfoValid = true;

  teamList: DelegateSummaryModel[] = null;
  teamListPromise: Promise<DelegateSummaryModel[]> = null;
  teamListValid = true;

  progress: ProgressModel = null;
  progressPromise: Promise<ProgressModel> = null;
  progressValid = true;

  managers: DelegateSimpleModel[] = [];
  managerId = 0;

  platform = 'website';

  layout: number = 0;
  isCoachingEnabled = false;
  isReviewEnabled = false;
  isHyperboundEnabled = false;
  isMyTasksEnabled = false;
  isManagerTasksEnabled = false;
  isCallTeamDashboardEnabled = false;

  constructor(
    private event: EventsService,
    public authService: AuthService,
    public userService: UserService,
    public teamService: TeamService,
    private progressService: ProgressService,
    private analytics: AnalyticsService,
    private router: Router,
    private platformService: Platform,
    private nav: NavService
  ) {
    this.environment = environment;
    this.platform = this.authService.appMobile ? 'mobile' : 'website';

    this.subscriptions.push(
      this.event.subscribe('login', () => {
        this.userInfoValid = false;
        this.teamListValid = false;
        this.progressValid = false;
      })
    );
    this.subscriptions.push(
      this.event.subscribe('logout', () => {
        this.userInfoValid = false;
        this.teamListValid = false;
        this.progressValid = false;
      })
    );
  }

  ngOnInit(): void {
    this.teamService.getMyManagersList()
      .then(managers => {
        this.managers = managers;
      });
  }

  ionViewWillEnter() {
    this.userType = this.authService.getUserType();

    if (this.teamList === null || !this.teamListValid) {
      this.teamListPromise = this.teamService.getMyTeamList(this.managerId);
    }

    if (this.userInfo === null || !this.userInfoValid) {
      if (this.userInfoValid && this.userService.userInfo !== null) {
        this.initUserInfo(this.userService.userInfo);
      } else {
        this.userInfoPromise = this.userService.getUserInfo();
      }
    }

    if (this.progress === null || !this.progressValid) {
      if (this.progressValid && this.progressService.progress !== null) {
        this.progress = this.progressService.progress;
      } else {
        this.progressPromise = this.progressService.getMyNotifications();
      }
    }
  }

  ionViewDidEnter() {
    this.analytics.trackView('Personal Development Review');

    if (this.teamList === null || !this.teamListValid) {
      this.teamListPromise.then(data => {
        this.teamList = data;
        this.teamListValid = true;
      });
    }

    if (this.userInfo === null || !this.userInfoValid) {
      this.userInfoPromise.then(data => {
        this.initUserInfo(data);
      });
    }

    if (this.progress === null || !this.progressValid) {
      this.progressPromise.then(data => {
        this.progress = data;
        this.progressValid = true;
      });
    }
  }

  initUserInfo(userData: UserInfoModel) {
    this.userInfo = userData;
    this.userInfoValid = true;

    this.layout = 0;

    this.layout += 1;
    this.layout += 1;
    this.isMyTasksEnabled = true;
    this.isManagerTasksEnabled = true;

    if (this.userInfo.company.enabled_review) {
      this.layout += 1;
      this.isReviewEnabled = true;
    }

    if (this.userInfo.company.enabled_coaching) {
      this.layout += 1;
      this.isCoachingEnabled = true;
    }

    // Hyperbound is enabled only for specific company
    if (this.userInfo.company.enabled_aicoach) {
      // this.layout += 1;
      // this.isHyperboundEnabled = true;

      this.isCallTeamDashboardEnabled = true;
    }
  }

  getGridTemplateColumns() {
    const screenWidth = window.innerWidth;
    const firstColumnFraction = screenWidth >= 719 ? '3fr' : '2.5fr';
    return `${firstColumnFraction} repeat(${this.layout}, 1fr)`;
  }

  selectFilter(managerId) {
    this.managerId = managerId;

    this.teamList = null;
    this.teamService.getMyTeamList(this.managerId).then(data => {
      this.teamList = data;
      this.teamListValid = true;
    });

    /* Update manager selector options */
    this.teamService.getMyManagersList(managerId).then(fetchedManagers => {
      const indentPrefix = managerId ? '- ' : '';

      const baseManagers = this.managers.filter(
        m => m.id === 0 || m.id === managerId
      ).map(
        m => ({
          ...m,
          full_name: m.full_name.replace(/^- /, '')
        })
      );

      const updatedManagers = [
        ...baseManagers,
        ...fetchedManagers.map(m => ({
          ...m,
          full_name: indentPrefix + m.full_name
        }))
      ];

      this.managers = updatedManagers;
    });
  }

  goToCoachingJourney(event, item: DelegateSummaryModel) {
    event.stopPropagation();

    if (item !== null) {
      this.nav.navigate(['/coach/journey/profile/', item.delegate.id]);
    }
  }

  goToReviewReport(event, item: DelegateSummaryModel) {
    event.stopPropagation();

    if (item !== null) {
      this.nav.navigate(['/review/team/', item.delegate.id]);
    }
  }

  goDetails(event, item: DelegateSummaryModel) {
    event.stopPropagation();

    if (item.delegate.template_id > 0) {
      this.router.navigate(['/team/details/', item.delegate.id]);
    }
  }

  goToReviewCallsHistory(event, item: DelegateSummaryModel) {
    event.stopPropagation();

    if (item.delegate.template_id > 0) {
      this.router.navigate(['/team/calls/', item.delegate.id]);
    }
  }

  goCallsTeamDashboard(event) {
    event.stopPropagation();

    this.router.navigate(['/calls/team/dashboard/']);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(i => i.unsubscribe());
  }
}
